import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for auth header
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem("token");
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // Set up response interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const storeAuthentication = (response) => {
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    return storeAuthentication(response);
  };

  const requestOtp = async (email) => {
    const response = await axios.post(`${API}/auth/request-otp`, { email });
    return response.data;
  };

  const verifyOtp = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, code });
    return storeAuthentication(response);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.roles?.includes("admin") || user?.role === "admin";
  const isAuthenticated = !!user;
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const hasRole = (role) => roles.includes("admin") || roles.includes(role);
  const isFinancialAnalyst = hasRole("financial_analyst");
  const isSupplyChainAnalyst = hasRole("supply_chain_analyst");
  const isPurchasingAnalyst = hasRole("purchasing_analyst");
  const isPlanningAnalyst = hasRole("planning_analyst");
  const isDailyReportUser = hasRole("daily_report") || hasRole("planning_analyst");
  const isGSTReportUser = hasRole("gst_report") || hasRole("financial_analyst");

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        requestOtp,
        verifyOtp,
        logout,
        isAdmin,
        isAuthenticated,
        roles,
        hasRole,
        isFinancialAnalyst,
        isSupplyChainAnalyst,
        isPurchasingAnalyst,
        isPlanningAnalyst,
        isDailyReportUser,
        isGSTReportUser,
        refreshUser: fetchUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
